import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { Fragment } from "react";
import {
  formatProposalChangeDetail,
  proposalChangeDetailText,
  proposalChangeHeadline,
  proposalChangeTransitionCarriesItemLabel,
  type ProposalChangeDescriptor,
} from "work/proposals/negotiation/proposal-change-description";
import { groupProposalChangesBySection } from "work/proposals/negotiation/proposal-change-sections";
import ProposalChangeValueRedline from "work/proposals/negotiation/proposal-change-value-redline";
import ProposalFeeScheduleChangeRedline from "work/proposals/negotiation/proposal-fee-schedule-change-redline";
import {
  partitionProposalFeeScheduleChanges,
  summarizeProposalFeeScheduleDescriptors,
} from "work/proposals/negotiation/proposal-fee-schedule-change-summary";

/**
 * The topical change list a Save confirmation shows, drawn the way the field's own change history
 * draws it: subject line, then the value transition as a redline.
 *
 * These dialogs used to pass a pre-joined `"• line\n• line"` string, which the confirm dialog rendered
 * inside a <p> — so every bullet collapsed into one run-on paragraph AND the redline was gone, since a
 * string cannot carry one. The descriptors were already structured; only the rendering threw that away.
 */

const ChangeList = styled("ul")(({ theme }) => ({
  listStyle: "none",
  margin: 0,
  paddingLeft: 0,
  display: "grid",
  gap: theme.spacing(0.75),
}));

// One line per change, exactly as the field's own change history writes it: "Added Fee Schedule
// Category: Standalone From Existing Category". Laying the headline and value out as separate grid rows
// split a single sentence across two lines, so the list read as a stack of fragments rather than as the
// change history's own phrasing.
const ChangeItem = styled("li")(({ theme }) => ({
  paddingLeft: theme.spacing(1.5),
  position: "relative",
  "&::before": {
    content: '"•"',
    left: 0,
    position: "absolute",
  },
}));

export type ProposalChangeSummaryListProps = {
  heading: string;
  descriptors: readonly ProposalChangeDescriptor[];
};

export default function ProposalChangeSummaryList({
  heading,
  descriptors,
}: Readonly<ProposalChangeSummaryListProps>) {
  if (descriptors.length === 0) return null;
  const sections = groupProposalChangesBySection(descriptors);

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" component="h3">
        {heading}
      </Typography>
      {sections.map((section) => {
        // The whole fee schedule as one description of itself, drawn exactly as the change history
        // draws it, instead of one bullet per semantic leaf.
        const { feeScheduleChanges, otherChanges } = partitionProposalFeeScheduleChanges(section.changes);
        return (
        <Fragment key={section.title}>
          <Typography variant="overline" component="h4" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
            {section.title}
          </Typography>
          <ChangeList aria-label={section.title}>
            {feeScheduleChanges.length > 0 && (
              <ChangeItem key="fee-schedule">
                <ProposalFeeScheduleChangeRedline
                  summary={summarizeProposalFeeScheduleDescriptors(feeScheduleChanges)}
                />
              </ChangeItem>
            )}
            {otherChanges.map((descriptor) => {
          // Headline and value come from the shared vocabulary, so this list and the change history
          // cannot word the same change differently — only the presentation differs, the redline here
          // standing in for the history's struck-through/blue value pair.
          const hasDetail = proposalChangeDetailText(descriptor) !== undefined;
          const transition = formatProposalChangeDetail(descriptor);
          // An added collection item's label is also its new value. It must still be drawn as an
          // insertion, even though the formatted transition and item label are the same string. The
          // item-label suppression below then keeps that value from being printed twice.
          const addedItemLabelIsTheNewValue =
            descriptor.action === "added" && proposalChangeTransitionCarriesItemLabel(descriptor);
          const showsRedline =
            transition !== undefined &&
            (transition !== descriptor.itemLabel || addedItemLabelIsTheNewValue);
          // ...and the item's name only when that redline is not already carrying it. A rename's new
          // value is the very label this would print, so drawing both said the name twice. One shared
          // rule with `proposalChangeDetailText`, so the list and the text form cannot disagree.
          const showsItemLabel =
            descriptor.itemLabel !== undefined &&
            !(showsRedline && proposalChangeTransitionCarriesItemLabel(descriptor));
          return (
            <ChangeItem key={descriptor.key}>
              <Typography variant="body2" component="span">
                {proposalChangeHeadline(descriptor)}
                {hasDetail && ": "}
                {showsItemLabel && descriptor.itemLabel}
                {showsItemLabel && showsRedline && " "}
                {showsRedline && (
                  <ProposalChangeValueRedline
                    previousValue={descriptor.previousText}
                    nextValue={descriptor.nextText}
                  />
                )}
              </Typography>
            </ChangeItem>
          );
            })}
          </ChangeList>
        </Fragment>
        );
      })}
    </Box>
  );
}
